import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshMe } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0 });
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", message: "Missing session_id" }); return; }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      polls.current += 1;
      try {
        const s = await api.billingCheckoutStatus(sessionId);
        if (s.payment_status === "paid") {
          await refreshMe?.();
          setState({ status: "ok" });
          setTimeout(() => nav("/app/settings", { replace: true }), 1500);
          return;
        }
        if (["failed", "expired"].includes(s.payment_status)) {
          setState({ status: "error", message: `Payment ${s.payment_status}` });
          return;
        }
      } catch (e) {
        // transient — keep polling
      }
      if (polls.current >= 20) {
        setState({ status: "timeout" });
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-forest text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-4" data-testid="payment-success">
        {state.status === "polling" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-coral" />
            <h1 className="font-display text-3xl">Finishing up your subscription…</h1>
            <p className="text-white/70 text-sm">Stripe is confirming your trial. This usually takes a couple of seconds.</p>
          </>
        )}
        {state.status === "ok" && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-emerald-300" />
            <h1 className="font-display text-3xl">You're on your trial.</h1>
            <p className="text-white/70 text-sm">14 days free. Managing your plan from Settings → Billing…</p>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <h1 className="font-display text-3xl">Still processing</h1>
            <p className="text-white/70 text-sm">Stripe is taking a moment. Head to <Link to="/app/settings" className="text-coral underline">Settings → Billing</Link> in a minute; your plan will show up there.</p>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle size={40} className="mx-auto text-coral" />
            <h1 className="font-display text-3xl">Something went wrong</h1>
            <p className="text-white/70 text-sm">{state.message}. <Link to="/pricing" className="text-coral underline">Back to plans</Link></p>
          </>
        )}
      </div>
    </div>
  );
}
